import { useEffect, useRef, forwardRef, useCallback } from "react";
import IMask from "imask";
import { parseDateStringToValidDate } from "../../utils/utils";

export const MaskedInput = forwardRef(
  (
    {
      value,
      onClick,
      onChange,          // will be called for react-datepicker internal syncing
      onClear,
      onBlur,            // parent blur handler
      handleDateChange,  
      setKey,
      questionId,
      closeCalendar,
    },
    ref
  ) => {
    const inputRef = useRef(null);
    const maskRef = useRef(null);
    const dirtyRef = useRef(false); // user modified input since last commit

    const getCurrentRawValue = useCallback(() => {
      const el = inputRef.current;
      if (!el) return "";
      return el.value.replace(/_/g, "").trim();
    }, []);

    const commitIfNeeded = useCallback(
      (rawValue) => {
        // Do nothing if user didn’t change anything since last commit
        if (!dirtyRef.current) return;

        const currentValue = (rawValue ?? getCurrentRawValue()).trim();

        // treat empty mask as cleared
        if (
          currentValue === "" ||
          currentValue === "--" ||
          currentValue === "____-__-__"
        ) {
          onClear?.();
          dirtyRef.current = false;
          return;
        }

        const parsedDate = parseDateStringToValidDate(currentValue);

        if (parsedDate) {
          handleDateChange(parsedDate);
        } else {
          // clear invalid
          onClear?.();
          // return;
        }

        dirtyRef.current = false;
      },
      [getCurrentRawValue, handleDateChange, onClear]
    );

    const handleKeyDown = (e) => {
      // ENTER => commit + refresh DatePicker input key (as you did)
      if (e.key === "Enter") {
        e.preventDefault();
        commitIfNeeded(getCurrentRawValue());
        setKey?.((prevKey) => prevKey + 1);
        return;
      }

      // TAB => let focus move, but commit + close calendar first
      if (e.key === "Tab") {
        closeCalendar?.();
        commitIfNeeded(getCurrentRawValue());
        // do not preventDefault
      }
    };

    const handleLocalChange = (e) => {
      dirtyRef.current = true;

      // let react-datepicker know input changed
      onChange?.(e);
    };

    const handleLocalBlur = (e) => {
      // commit on blur
      commitIfNeeded(e.target.value);

      // still call parent's onBlur if provided
      onBlur?.(e);
    };

    useEffect(() => {
      if (!inputRef.current) return;

      maskRef.current = IMask(inputRef.current, {
        mask: "0000-00-00", // YYYY-MM-DD
        lazy: false,
        placeholderChar: "_",
      });

      maskRef.current.on("accept", () => {
        // mark as dirty when user edits via mask
        dirtyRef.current = true;

        // trigger input event for DatePicker internal listeners
        const event = new Event("input", { bubbles: true });
        inputRef.current.dispatchEvent(event);
      });

      return () => {
        maskRef.current?.destroy();
        maskRef.current = null;
      };
    }, []);

    useEffect(() => {
      // Commit when clicking outside input (mouse)
      const onDocumentMouseDown = (ev) => {
        const el = inputRef.current;
        if (!el) return;

        // If click is outside the input, commit and close
        if (ev.target instanceof Node && !el.contains(ev.target)) {
          closeCalendar?.();

          // If input is focused, blur will fire and commit there.
          // If not focused (edge cases), commit directly.
          if (document.activeElement === el) {
            el.blur();
          } else {
            commitIfNeeded();
          }
        }
      };

      document.addEventListener("mousedown", onDocumentMouseDown, true);
      return () => {
        document.removeEventListener("mousedown", onDocumentMouseDown, true);
      };
    }, [closeCalendar, commitIfNeeded]);

    return (
      <input
        ref={(el) => {
          inputRef.current = el;
          if (typeof ref === "function") ref(el);
          else if (ref) ref.current = el;
        }}
        type="text"
        className="form-control"
        id={`QUESTION_${questionId}`}
        placeholder="YYYY-MM-DD"
        value={value || ""}
        onClick={onClick}
        onChange={handleLocalChange}
        onBlur={handleLocalBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }
);
